import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface Dashboard {
  readonly dashboardId: string;
  readonly dashboardCode: string;
  readonly dashboardName: string;
  readonly isPublished: boolean;
}

/**
 * M26 — Executive Dashboard (§13, §31). Lists executive dashboards and lets a
 * steward publish one. Read-only intelligence; publishing is audited (BR-07).
 */
export function M26ExecutiveDashboardPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<Dashboard[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: Dashboard[]; total: number }>('/v1/dwh/dashboards', { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, reloadKey]);

  async function publish(id: string): Promise<void> {
    try { await apiRequest(`/v1/dwh/dashboards/${id}/publish`, { method: 'POST', body: {}, token }); setReloadKey((k) => k + 1); }
    catch (e) { setError((e as Error).message); }
  }

  return (
    <main aria-label="executive-dashboard">
      <h1>{t('m26.executive.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m26.executive.loading')}</p> : (
        <>
          <p aria-label="dashboard-count">{t('m26.executive.count', { count: total })}</p>
          <table aria-label="dashboard-table">
            <thead><tr><th>{t('m26.executive.colCode')}</th><th>{t('m26.executive.colName')}</th><th>{t('m26.executive.colStatus')}</th><th></th></tr></thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.dashboardId}>
                  <td>{d.dashboardCode}</td><td>{d.dashboardName}</td>
                  <td>{d.isPublished ? t('m26.executive.published') : t('m26.executive.draft')}</td>
                  <td>{!d.isPublished && <button type="button" onClick={() => publish(d.dashboardId)}>{t('m26.executive.publish')}</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
